import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CpMarketplaceApiService } from '../../../services/cp-marketplace-api.service';
import { ISSUER_MESSAGES } from 'src/app/messages';
import { AllDocsAdditionalData, DocumentAdditionalDataValidity, DocumentKeys, FileTypes } from 'appRootPath/models/commercial-papers.model';
import {
  API_DATE_FORMAT,
  DOCUMENT_NAME_MAP,
  FileObject,
  MODAL_OPTIONS,
  UI_DATE_FORMAT_MAP
} from 'appRootPath/shared/global-meta-data';
import { ViewDocsModalComponent } from 'appRootPath/shared/view-docs-modal/view-docs-modal.component';
import { getCalendarDateObj, getDateFromCalendarObj, isWeekendAndHolidayDisabled } from 'appRootPath/utils';
import * as moment from 'moment';
import { CpDocumentationActionsService } from 'appRootPath/services/cp-documentation-actions.service';

@Component({
  selector: 'app-upload-letters-modal',
  templateUrl: './upload-letters-modal.component.html',
  styleUrls: ['./upload-letters-modal.component.scss']
})
export class UploadLettersModalComponent implements OnInit {

  title = 'Upload Letters';
  @Input() docKey: DocumentKeys;
  @Input() issueId: string;
  @Input() prefillData: AdditionalInformation;
  @Input() docDateValidityMap: DocumentAdditionalDataValidity | null;

  documentDate = null;
  fileTypes: FileTypes[] = ['.pdf'];
  additionalAttachment = null;
  attachmentDescription = '';
  existingDoc: FileObject = null;

  looDateObj;
  looDateFormatted;
  issueValueDateObj;
  issueValueDateFormatted;
  isWeekendAndHolidayDisabled = isWeekendAndHolidayDisabled;

  constructor(
    public activeModal: NgbActiveModal,
    private toastr: ToastrService,
    private apiService: CpMarketplaceApiService,
    private modalService: NgbModal,
    private documentationService: CpDocumentationActionsService
  ) { }

  ngOnInit() {
    this.setModalTitle(this.docKey);

    if (this.prefillData) {
      this.setPrefillData(this.prefillData);
    }

    [this.looDateFormatted, this.looDateObj, this.issueValueDateFormatted, this.issueValueDateObj] =
      this.documentationService.initDatesForValidation(this.docDateValidityMap);
  }

  setModalTitle(docKey) {
    this.title = DOCUMENT_NAME_MAP[docKey];
  }

  setPrefillData(prefillData: AdditionalInformation) {
    if (prefillData.additional_doc) {
      this.additionalAttachment = prefillData.additional_doc.file_key;
    }
    if (prefillData && prefillData.document_date) {
      this.documentDate = getCalendarDateObj(moment(this.prefillData.document_date).toDate());
    }

    this.existingDoc = prefillData.additional_doc;
    this.attachmentDescription = prefillData.description;
  }

  setAdditionalDocs(fileContent) {
    if (fileContent) {
      const formDataRequest = new FormData();
      formDataRequest.append('files', fileContent);

      this.apiService.uploadFileAndGetToken(formDataRequest).subscribe(resp => {
        if (resp) {
          this.toastr.success(ISSUER_MESSAGES.ADDN_ATTACHMENT_DONE);

          // uploadFileAndGetToken api will always return an array of files.
          // Since we are sending only one document, we are reading only the first document from resp.
          this.additionalAttachment = resp[0];
          this.existingDoc = null;
        }
      });
    }
  }

  setAdditionalData(documentDateModelErrors) {
    if (documentDateModelErrors) {
      this.toastr.warning(ISSUER_MESSAGES.INVALID_FORM);
    } else {
      const additionalDataJSON: UploadLettersAdditionalData = {
        issue: this.issueId,
        [this.docKey]: {
          additional_doc: this.additionalAttachment,
          description: this.attachmentDescription,
          document_date: getDateFromCalendarObj(this.documentDate).format(API_DATE_FORMAT)
        }
      };

      this.apiService.postAdditionalData(additionalDataJSON).
        subscribe((res: AllDocsAdditionalData) => {
          if (res.id) {
            // !ToDo get strings from common messages file by unique reference.
            this.toastr.success(`${this.title} ${ISSUER_MESSAGES.ADDN_DATA_SAVED}`);
            this.activeModal.close(res[this.docKey]);
          }
        });
    }
  }

  showDoc(docUrl) {
    this.apiService.fetchFile(docUrl).subscribe(fileResp => {
      const modalRef = this.modalService.open(ViewDocsModalComponent, MODAL_OPTIONS);
      modalRef.componentInstance.docsToView = [{
        title: `${this.title} - Additional Attachment`,
        document_url: fileResp
      }];
    });
  }
}

interface UploadLettersAdditionalData {
  issue: string;
  covering_letter?: AdditionalInformation;
  eligibility_letter?: AdditionalInformation;
}

interface AdditionalInformation {
  additional_doc: FileObject;
  description: string;
  document_date: string;
}